import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../features/collections/redux/actions';
import * as commonActions from '../../features/common/redux/actions';
import CollectionHeaderNav from './CollectionHeaderNav';
import types from '../../common/formElements/CollectionSectionType';
import CustomLoader from '../common/CustomLoader';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import ParentCard from '../common/ParentCard';
import { BelongsCreatedByRefField, byFields, pluginCode } from 'drapcode-constant';
import CustomSelect from '../home/collections/CustomSelect';
import CustomRadio from '../../common/ui/CustomRadio';
import { showAlertHtml } from '../../common/utils/alert';
import { toast } from 'react-toastify';

export class TypesenseMapping extends Component {
  static propTypes = {
    collections: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    commonActions: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { match, actions } = this.props;
    const { params } = match;
    const { projectId, collectionId, version } = params;
    actions.loadProjectCollections({ projectId, version });
    actions.loadProjectCollectionDetail({ projectId, collectionId, version });
    actions.loadProjectPlugins({ projectId, version });
  }

  handleSubmit = (values) => {
    const { collections, match, actions, commonActions } = this.props;
    const { selectedCollection, projectCollections } = collections;
    const { projectId, version } = match.params;
    const transformedValues = Object.keys(values)
      .filter((key) => !key.endsWith('_priority') && !key.endsWith('_allowedReferenceFields'))
      .map((key) => {
        const schemaValue = values[key];
        const priorityValue = values[`${key}_priority`] ? values[`${key}_priority`].value : 1;
        if (!schemaValue) return null;
        const field = selectedCollection.fields.find((f) => f.fieldName === key);
        if (!field) return null;
        const fieldType = field.type || '';
        const fieldTitle = field.fieldTitle.en || '';
        const allowedRefKey = `${key}_allowedReferenceFields`;
        const allowedReferenceFields = Array.isArray(values[allowedRefKey])
          ? values[allowedRefKey].map((opt) => opt.value)
          : [];
        let fileReferenceFields = [];
        let nestedReferenceFields = [];
        if ([...BelongsCreatedByRefField, ...byFields].includes(fieldType)) {
          const { refCollection } = field;
          const { collectionName } = refCollection || {};
          const refCollectionDef = projectCollections.find(
            (c) => c.collectionName === collectionName,
          );
          if (refCollectionDef) {
            fileReferenceFields = allowedReferenceFields.filter((refFieldName) => {
              const refField = refCollectionDef.fields.find((f) => f.fieldName === refFieldName);
              return refField.type === 'file';
            });
            nestedReferenceFields = allowedReferenceFields.filter((refFieldName) => {
              const refField = refCollectionDef.fields.find((f) => f.fieldName === refFieldName);
              return [...BelongsCreatedByRefField, ...byFields].includes(refField.type);
            });
          }
        }
        const maxLinkedItemsKey = `${key}_maxLinkedItems`;
        const maxLinkedItems = values[maxLinkedItemsKey];
        const isLinkedField = [...BelongsCreatedByRefField, ...byFields, 'file'].includes(
          fieldType,
        );
        const fieldPayload = {
          fieldName: key,
          enableSchema: schemaValue,
          priority: priorityValue,
          fieldType,
          fieldTitle,
        };
        if ([...BelongsCreatedByRefField, ...byFields].includes(fieldType)) {
          fieldPayload.allowedReferenceFields =
            allowedReferenceFields.length > 0 ? allowedReferenceFields : ['uuid'];
        }
        if (isLinkedField) {
          fieldPayload.maxLinkedItems = maxLinkedItems || 1;
        }
        if (fileReferenceFields.length > 0) {
          fieldPayload.fileReferenceFields = fileReferenceFields;
        }
        if (nestedReferenceFields.length > 0) {
          fieldPayload.nestedReferenceFields = nestedReferenceFields;
        }
        return fieldPayload;
      })
      .filter(Boolean);
    if (transformedValues.length === 0) {
      console.warn('No valid fields to submit.');
      toast.error('Please add at least one field to the schema before saving.');
      return;
    }
    const data = { typesenseMapping: transformedValues };
    const preConfirm = () => {
      actions.createTypesenseMappingInCollection({
        projectId,
        version,
        data,
        selectedCollection,
      });
      commonActions.trackCollectionChange({
        uuid: selectedCollection.uuid,
        name: selectedCollection.collectionName,
      });
    };

    showAlertHtml(
      selectedCollection.typesenseMapping.length
        ? `Are you sure you want to update the Typesense mapping?<br/><br/>
       <span>Updating the mapping will require reindexing the data to reflect the changes.</span>`
        : `Save the Typesense mapping?<br/><br/>
       <span>To include existing data in Typesense, use the "Reindex Data for Typesense Collection" event after saving.</span>`,
      preConfirm,
      selectedCollection.typesenseMapping.length
        ? 'Yes, update the mapping'
        : 'Yes, save the mapping',
      selectedCollection.typesenseMapping.length ? 'warning' : 'info',
    );
  };

  getInitialValues = (selectedCollection) => {
    return selectedCollection.fields.reduce((acc, field) => {
      const fieldName = field.fieldName;
      const typesenseMapping = selectedCollection.typesenseMapping.find(
        (item) => item.fieldName === fieldName,
      );
      acc[fieldName] = typesenseMapping ? typesenseMapping.enableSchema || '' : '';
      acc[`${fieldName}_priority`] = typesenseMapping
        ? {
            label: typesenseMapping.priority.toString() || '1',
            value: typesenseMapping.priority || 1,
          }
        : '';
      const isReferenceField = [...BelongsCreatedByRefField, ...byFields].includes(field.type);
      if (
        isReferenceField &&
        typesenseMapping &&
        typesenseMapping.allowedReferenceFields &&
        typesenseMapping.allowedReferenceFields.length
      ) {
        acc[`${fieldName}_allowedReferenceFields`] = typesenseMapping.allowedReferenceFields.map(
          (refFieldName) => ({
            value: refFieldName,
            label: this.getFieldLabel(refFieldName, selectedCollection.fields),
          }),
        );
      }
      const isLinkedField = [...BelongsCreatedByRefField, ...byFields, 'file'].includes(field.type);
      if (isLinkedField && typesenseMapping && typesenseMapping.maxLinkedItems) {
        acc[`${fieldName}_maxLinkedItems`] = typesenseMapping.maxLinkedItems;
      }
      return acc;
    }, {});
  };

  getFieldLabel(fieldName, allFields) {
    if (!Array.isArray(allFields) || !fieldName) {
      return fieldName || '';
    }
    const field = allFields.find((f) => f.fieldName === fieldName);
    if (!field) {
      return fieldName;
    }
    return (field.fieldTitle && field.fieldTitle.en) || field.fieldName || fieldName;
  }

  renderAllowedRefFields(field, values, projectCollections, setFieldValue, setFieldTouched) {
    const fieldName = field.fieldName;
    const isRefField = [...BelongsCreatedByRefField, ...byFields].includes(field.type);
    const refFieldOptions = [];
    if (isRefField) {
      const { refCollection } = field || {};
      const { collectionName } = refCollection || {};
      const selectedRefCollection =
        projectCollections.find((collection) => collection.collectionName === collectionName) || '';
      const refCollectionFields = selectedRefCollection ? selectedRefCollection.fields || [] : [];
      refCollectionFields.forEach((refField) => {
        if (refField.fieldName !== fieldName) {
          refFieldOptions.push({
            value: refField.fieldName,
            label: refField.fieldTitle.en,
          });
        }
      });
    }
    return (
      <div className="col-md col-lg col-sm-12 col-xs-12">
        {isRefField && (
          <CustomSelect
            name={`${field.fieldName}_allowedReferenceFields`}
            isMulti={true}
            value={values[`${field.fieldName}_allowedReferenceFields`]}
            options={refFieldOptions}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
            isDisabled={values[field.fieldName] && isRefField ? false : true}
            placeholder="Select Reference Fields"
          />
        )}
      </div>
    );
  }

  renderMaxLinkedItemsField(field, values, setFieldValue, setFieldTouched) {
    const fieldName = field.fieldName;
    const isLinkedField = [...BelongsCreatedByRefField, ...byFields, 'file'].includes(field.type);
    return (
      <div className="col-md col-lg col-sm-12 col-xs-12">
        {isLinkedField && (
          <input
            type="number"
            min="1"
            className="form-control"
            name={`${fieldName}_maxLinkedItems`}
            value={values[`${fieldName}_maxLinkedItems`] || ''}
            onChange={(e) =>
              setFieldValue(`${fieldName}_maxLinkedItems`, parseInt(e.target.value, 10))
            }
            onBlur={() => setFieldTouched(`${fieldName}_maxLinkedItems`, true)}
            disabled={!values[fieldName]}
            placeholder="Enter max linked items"
          />
        )}
      </div>
    );
  }

  render() {
    const { collections, match } = this.props;
    const { selectedCollection, projectCollections } = collections;
    const { projectId, version } = match.params;
    const { loadProjectCollectionsPending, loadProjectCollectionDetailPending, plugins } =
      collections;
    const hasTypesensePlugin = plugins.some(
      (plugin) => plugin.code === pluginCode.TYPESENSE_SEARCH,
    );
    if (loadProjectCollectionsPending || loadProjectCollectionDetailPending) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.TYPESENSE_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <div className="text-center">
            <CustomLoader
              loading={loadProjectCollectionsPending || loadProjectCollectionDetailPending}
              size="20px"
              color="#303453"
            />
          </div>
        </CommonPageComponent>
      );
    }

    if (!hasTypesensePlugin) {
      return (
        <CommonPageComponent>
          <CollectionHeaderNav
            label={types.TYPESENSE_MAPPING}
            projectId={projectId}
            version={version}
            selectedCollection={selectedCollection}
            loadProjectCollectionsPending={loadProjectCollectionsPending}
            loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
          />
          <ParentCard
            icon="typesenseMapping"
            header={`Typesense Mapping ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create Typesense Mapping for collection."
          >
            <div className="text-center">
              <h5>Please install the Typesense Search plugin to manage mapping.</h5>
            </div>
          </ParentCard>
        </CommonPageComponent>
      );
    }

    const initialValues = this.getInitialValues(selectedCollection);
    return (
      <CommonPageComponent>
        <CollectionHeaderNav
          label={types.TYPESENSE_MAPPING}
          projectId={projectId}
          version={version}
          selectedCollection={selectedCollection}
          loadProjectCollectionsPending={loadProjectCollectionsPending}
          loadProjectCollectionDetailPending={loadProjectCollectionDetailPending}
        />
        {selectedCollection && (
          <ParentCard
            icon="typesenseMapping"
            header={`Typesense Mapping ${
              selectedCollection && ` For ${selectedCollection.collectionTitle}`
            }`}
            subtitle="Create Typesense Mapping for a collection."
          >
            <Formik initialValues={initialValues} onSubmit={this.handleSubmit}>
              {({ setFieldValue, setFieldTouched, values }) => {
                const fieldCount = selectedCollection.fields.length;
                const priorityOptions = Array.from({ length: fieldCount }, (_, i) => i + 1);
                return (
                  <Form className="form-horizontal">
                    <div className="row">
                      <div className="col">
                        <h5>Collection Fields ({selectedCollection.fields.length})</h5>
                      </div>
                      <div className="col">
                        <h5>Typesense Schema</h5>
                      </div>
                      <div className="col">
                        <h5>Reference Fields</h5>
                      </div>
                      <div className="col">
                        <h5>Max Number of Linked Items</h5>
                      </div>
                      <div className="col">
                        <h5>Priority</h5>
                      </div>
                    </div>
                    {selectedCollection.fields.map((field) => (
                      <div className="row mx-auto mb-3" key={field.fieldName}>
                        <div className="col-md col-lg col-sm-12 col-xs-12">
                          <span className="input-group-text">{field.fieldTitle.en}</span>
                        </div>
                        <div className="col-md col-lg col-sm-12 col-xs-12">
                          <div className="form-check">
                            <CustomRadio label="Add in Schema" isRadio={false}>
                              <Field
                                type="checkbox"
                                name={field.fieldName}
                                className="form-check-input"
                              />
                            </CustomRadio>
                            <small>( Add in Typesense Schema.)</small>
                            <p className="errors">
                              <ErrorMessage name={field.fieldName} />
                            </p>
                          </div>
                        </div>
                        {this.renderAllowedRefFields(
                          field,
                          values,
                          projectCollections,
                          setFieldValue,
                          setFieldTouched,
                        )}
                        {this.renderMaxLinkedItemsField(
                          field,
                          values,
                          setFieldValue,
                          setFieldTouched,
                        )}
                        <div className="col-md col-lg col-sm-12 col-xs-12">
                          <CustomSelect
                            name={`${field.fieldName}_priority`}
                            isMulti={false}
                            value={values[`${field.fieldName}_priority`]}
                            options={priorityOptions.map((num) => ({
                              label: num,
                              value: num,
                            }))}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            placeholder="Select Priority"
                          />
                        </div>
                      </div>
                    ))}
                    <div className="text-right mt-3">
                      <button type="submit" className="btn btn-theme-submit btn-md">
                        {selectedCollection.typesenseMapping &&
                        selectedCollection.typesenseMapping.length ? (
                          <>
                            <i className="wb webconnect-save-regular"></i>&nbsp;Update&nbsp;
                          </>
                        ) : (
                          <>
                            <i className="wb webconnect-plus"></i>&nbsp;Save&nbsp;
                          </>
                        )}
                        <CustomLoader loading={false} />
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </ParentCard>
        )}
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    collections: state.collections,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
    commonActions: bindActionCreators({ ...commonActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TypesenseMapping);
